import axios from 'axios';
import { URL } from './url';
import { logErrorToCloud } from './cloudLogProxy';
import { generateIdempotencyKey } from './idempotencyKey';
import { SESSION_REPLACED_EVENT, isLaunchEnded, launchIdOf } from './launches';

function shouldLogHttpErrorsToCloud() {
  try {
    const v = process.env.REACT_APP_LOG_HTTP_ERRORS_TO_CLOUD;
    return typeof v === "string" && v.toLowerCase() === "true";
  } catch (_) {
    return false;
  }
}

function shouldLogHttpStatusToCloud(status) {
  // Default behavior stays conservative; enable all HTTP >= 400 only when explicitly opted-in.
  if (!shouldLogHttpErrorsToCloud()) return false;
  if (typeof status !== "number") return false;
  return status >= 400;
}

function shouldAttachIdempotencyKey(config) {
  try {
    const method = (config?.method || '').toLowerCase();
    const url = String(config?.url || '');
    if (method !== 'post') return false;
    // Backoffice POST create endpoints (collections)
    const patterns = [
      /\/backoffice\/v1\/courses\/$/,
      /\/backoffice\/v1\/courses\/\d+\/units\/$/,
      /\/backoffice\/v1\/units\/\d+\/(lessons|scenarios|info-pages)\/$/,
      /\/backoffice\/v1\/lessons\/\d+\/versions\/$/,
      /\/backoffice\/v1\/lesson-versions\/\d+\/steps\/$/,
      /\/backoffice\/v1\/scenarios\/\d+\/states\/$/,
      /\/backoffice\/v1\/scenarios\/\d+\/conditions\/$/,
      /\/backoffice\/v1\/scenario-conditions\/\d+\/outcomes\/$/,
      /\/backoffice\/v1\/scenario-states\/\d+\/state-conditions\/$/,
      // Common top-level collections
      /\/backoffice\/v1\/(skills|classification-questions|user-info-fields)\/$/,
      // Skills permissions collection
      /\/backoffice\/v1\/skills\/\d+\/permissions\/$/,
      // Admin backoffice collections
      /\/backoffice\/v1\/organizations\/$/,
      /\/backoffice\/v1\/invites\/$/,
      // Legacy create pattern support
      /\/backoffice\/v1\/.+\/create\/$/
    ];
    return patterns.some((re) => re.test(url));
  } catch (_) {
    return false;
  }
}

function debugLog(message, meta) {
  // eslint-disable-next-line no-console
  console.debug(`[apiClient] ${message}`, meta || {});
}

async function normalizeBlobErrorResponse(error) {
  try {
    const responseData = error?.response?.data;
    if (!(typeof Blob !== 'undefined' && responseData instanceof Blob)) {
      return;
    }

    const blobText = typeof responseData.text === 'function'
      ? await responseData.text()
      : '';
    const trimmedText = String(blobText || '').trim();
    if (!trimmedText) {
      return;
    }

    try {
      error.response.data = JSON.parse(trimmedText);
    } catch (_) {
      error.response.data = {
        error: {
          message: trimmedText,
        },
      };
    }
  } catch (_) {}
}

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: URL + "/api",
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  // withCredentials not needed for JWT authentication
});

// Add security headers
apiClient.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// ETag cache: maps canonical resource path to latest ETag
const etagCache = new Map();

function canonicalEtagKey(urlPath) {
  try {
    if (!urlPath) return null;
    // Ensure it starts with '/'
    let u = String(urlPath);
    // Normalize optional '/api' prefix found in absolute Location headers
    if (u.startsWith('/api/')) {
      u = u.slice(4);
    }
    // Normalize PATCH/DELETE resource endpoints: strip trailing action segment
    if (u.endsWith('/patch/')) return u.slice(0, -('patch/'.length));
    if (u.endsWith('/delete/')) return u.slice(0, -('delete/'.length));
    // Ordering endpoints require parent's ETag
    if (u.endsWith('/steps-order/')) {
      // /lesson-versions/{id}/steps-order/ -> /lesson-versions/{id}/
      return u.replace(/\/steps-order\/$/, '/');
    }
    if (u.endsWith('/state-conditions-order/')) {
      // /scenario-states/{id}/state-conditions-order/ -> /scenario-states/{id}/
      return u.replace(/\/state-conditions-order\/$/, '/');
    }
    if (u.endsWith('/content-order/')) {
      // /units/{id}/content-order/ -> /units/{id}/
      return u.replace(/\/content-order\/$/, '/');
    }
  if (u.endsWith('/units-order/')) {
    // /courses/{id}/units-order/ -> /courses/{id}/
    return u.replace(/\/units-order\/$/, '/');
  }
    return u;
  } catch (_) {
    return null;
  }
}

// Request interceptor to add JWT token to headers
apiClient.interceptors.request.use(
  async (config) => {
    // A request on a launch this page has ended is not sent (launches.js).
    if (isLaunchEnded(launchIdOf(config))) {
      throw new axios.CanceledError('The launch this request names has ended.');
    }

    // Ensure headers object exists before use
    if (!config.headers) {
      config.headers = {};
    }

    // When uploading files via FormData let the browser set the boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }

    // Add JWT token
    const hasWindow = typeof window !== 'undefined';
    const hasLocalStorage = hasWindow && typeof window.localStorage !== 'undefined';
    const token = hasLocalStorage ? window.localStorage.getItem('access_token') : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach Idempotency-Key for backoffice create POSTs if not present
    try {
      if (shouldAttachIdempotencyKey(config) && !config.headers['Idempotency-Key']) {
        const key = generateIdempotencyKey();
        config.headers['Idempotency-Key'] = key;
        debugLog('Attached Idempotency-Key', { url: config.url, key });
      }
    } catch (_) {}

    // Attach If-Match for backoffice PATCH/DELETE and ordering endpoints
    try {
      const method = (config?.method || '').toLowerCase();
      const url = String(config?.url || '');
      const isBackoffice = /\/backoffice\/v1\//.test(url);
      const needsIfMatch = (method === 'patch' || method === 'delete');
      
      // Ensure headers object exists before checking/modifying
      if (!config.headers) {
        config.headers = {};
      }
      
      if (isBackoffice && needsIfMatch && !config.headers['If-Match']) {
        const key = canonicalEtagKey(url);
        if (key && etagCache.has(key)) {
          config.headers['If-Match'] = etagCache.get(key);
          debugLog('Attached If-Match from cache', { url, etag: config.headers['If-Match'] });
        } else if (key) {
          // Best-effort prefetch of ETag if missing
          try {
            const hasWindow = typeof window !== 'undefined';
            const hasLocalStorage = hasWindow && typeof window.localStorage !== 'undefined';
            const token = hasLocalStorage ? window.localStorage.getItem('access_token') : null;
            const resp = await axios.get((apiClient.defaults.baseURL || '') + key, {
              ...(config.timeout ? { timeout: config.timeout } : {}),
              headers: {
                'Authorization': token ? `Bearer ${token}` : undefined,
                'X-Requested-With': 'XMLHttpRequest'
              }
            });
            const et = resp?.headers?.etag || resp?.headers?.ETag;
            if (et) {
              etagCache.set(key, et);
              config.headers['If-Match'] = et;
              debugLog('Prefetched and attached If-Match', { url, etag: et });
            }
          } catch (_) {}
        }
      }
    } catch (_) {}

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Variable to track if token refresh is in progress
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Response interceptor to handle token expiration and refresh
apiClient.interceptors.response.use(
  (response) => {
    // A late answer on a launch this page has ended is dropped.
    if (isLaunchEnded(launchIdOf(response?.config))) {
      return Promise.reject(new axios.CanceledError('The launch this request names has ended.'));
    }
    try {
      // Cache ETag for GET/POST/PATCH responses on backoffice endpoints
      const url = String(response?.config?.url || '');
      const method = (response?.config?.method || '').toLowerCase();
      const isBackoffice = /\/backoffice\/v1\//.test(url);
      const etag = response?.headers?.etag || response?.headers?.ETag;
      if (isBackoffice && etag && (method === 'get' || method === 'post' || method === 'patch')) {
        const key = canonicalEtagKey(url);
        if (key) etagCache.set(key, etag);
        debugLog('Cached ETag for response', { url, etag, method });
        // Also cache by Location (new resource URL) when present
        const location = response?.headers?.location || response?.headers?.Location;
        if (location) {
          const locKey = canonicalEtagKey(location);
          if (locKey) etagCache.set(locKey, etag);
          debugLog('Cached ETag by Location', { location, etag });
        }
        // Also cache by Content-Location (canonical resource URL) when present. The per-language
        // activation (CAS) endpoint POSTs to /<res>/{id}/activation/ but returns the CANONICAL
        // resource's ETag together with Content-Location = /<res>/{id}/. Without this branch the ETag
        // would be cached under the useless .../activation/ key and the next content PATCH on the
        // resource would send a stale If-Match and 412 (there is no auto-retry, by design). Mirror of
        // the Location branch above.
        const contentLocation = response?.headers?.['content-location'] || response?.headers?.['Content-Location'];
        if (contentLocation) {
          const clKey = canonicalEtagKey(contentLocation);
          if (clKey) etagCache.set(clKey, etag);
          debugLog('Cached ETag by Content-Location', { contentLocation, etag });
        }
      }
      return response;
    } catch (_) {
      return response;
    }
  },
  async (error) => {
    // A request cut off on purpose (an abort, or a launch the page ended) is no failure to report.
    if (axios.isCancel(error)) return Promise.reject(error);
    // A late failure on a launch this page has ended is dropped like a late answer, before any retry, event or
    // message: it must not reach the page that runs another launch.
    if (isLaunchEnded(launchIdOf(error?.config))) {
      return Promise.reject(new axios.CanceledError('The launch this request names has ended.'));
    }

    const originalRequest = error.config;

    await normalizeBlobErrorResponse(error);

    // Every request on a session another launch has closed is answered SESSION_REPLACED. The callers
    // do not all pass it on (a failed step image becomes a placeholder; a tool or annotation save
    // catches its own errors), so one page event tells the lesson page, naming the request's launch.
    // A request sent with `sessionReplacedEvent: false` (`lesson_end`) raises none.
    try {
      const apiError = error?.response?.data?.error;
      if (apiError?.code === 'SESSION_REPLACED' && originalRequest?.sessionReplacedEvent !== false) {
        window.dispatchEvent(new CustomEvent(SESSION_REPLACED_EVENT, {
          detail: { launchId: launchIdOf(originalRequest), openSession: apiError.open_session || null },
        }));
      }
    } catch (_) {}

    // Best-effort: forward only "real" unexpected API failures to backend logging.
    // - By default avoid logging auth/validation noise (401/403/404/412/422, etc.)
    // - If REACT_APP_LOG_HTTP_ERRORS_TO_CLOUD=true, forward ALL HTTP >= 400 as client error logs
    // - Avoid recursion on the /log endpoint itself
    try {
      const status = error?.response?.status;
      const url = String(originalRequest?.url || "");
      const isLogEndpoint = url.includes("/log");
      const isNetworkError = !error?.response;
      const isServerError = typeof status === "number" && status >= 500;
      const isHttpError = shouldLogHttpStatusToCloud(status);

      if (!isLogEndpoint && (isNetworkError || isServerError || isHttpError)) {
        const method = originalRequest?.method;
        const statusText = error?.response?.statusText;

        // Avoid logging request/response bodies; only include minimal metadata to reduce PII risk.
        // If you need more context, correlate with server logs by time/url.
        const msg =
          isNetworkError
            ? `Network error calling ${method || "request"} ${url}`
            : `HTTP ${status} ${statusText || ""} calling ${method || "request"} ${url}`.trim();

        logErrorToCloud(new Error(msg), {
          source: "axios",
          request: { url, method },
          response: status ? { status, statusText } : undefined,
          kind: isNetworkError ? "network_error" : "http_error",
        });
      }
    } catch (_) {}

    // Provide user-friendly messages for concurrency and validation
    try {
      const status = error?.response?.status;
      const serverMessage =
        error?.response?.data?.error?.message ||
        (typeof error?.response?.data?.error === 'string' ? error.response.data.error : null) ||
        error?.response?.data?.message ||
        error?.response?.data?.detail;
      if (status === 412) {
        error.userMessage = 'Your copy is stale. Please refresh and try again.';
      } else if (status === 422) {
        const code = error?.response?.data?.error?.code;
        const message = serverMessage || 'Validation failed';
        error.userMessage = code ? `${message} (${code})` : message;
      } else if (serverMessage) {
        error.userMessage = serverMessage;
      }
    } catch (_) {}

    // There is no 412 auto-retry, by design; the UI should GET to refresh the ETag before PATCH/DELETE

    if (error.response?.status === 401 && !originalRequest._retry) {
      console.warn('[apiClient] 401 Unauthorized - attempting token refresh', { 
        url: originalRequest.url, 
        method: originalRequest.method,
        isRefreshing 
      });
      const hasWindow = typeof window !== 'undefined';
      const hasLocalStorage = hasWindow && typeof window.localStorage !== 'undefined';
      const refreshToken = hasLocalStorage ? window.localStorage.getItem('refresh_token') : null;
      
      // If no refresh token, redirect to login
      if (!refreshToken) {
        console.error('[apiClient] No refresh token available, redirecting to login');

        if (hasLocalStorage) {
          window.localStorage.removeItem('access_token');
          window.localStorage.removeItem('refresh_token');
          try { window.dispatchEvent(new Event('storage')); } catch (_) {}
          if (window.location?.pathname !== '/login') {
            try { window.location.href = '/login'; } catch (_) {}
          }
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        console.log('[apiClient] Token refresh in progress, queueing request', { 
          url: originalRequest.url,
          queueLength: failedQueue.length 
        });
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(`${URL}/api/refresh-token/`, {
          refresh_token: refreshToken
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;
        
        console.log('[apiClient] Token refresh successful', { queueLength: failedQueue.length });
        
        // Store new tokens
        if (hasLocalStorage) {
          window.localStorage.setItem('access_token', access_token);
          if (newRefreshToken) {
            window.localStorage.setItem('refresh_token', newRefreshToken);
          }
        }
        
        // Update the Authorization header for the original request
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        
        // Process the queue with the new token
        processQueue(null, access_token);
        
        // Retry the original request
        return apiClient(originalRequest);
        
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        
        // Refresh failed, clear tokens and redirect to login
        if (hasLocalStorage) {
          window.localStorage.removeItem('access_token');
          window.localStorage.removeItem('refresh_token');
          window.dispatchEvent(new Event('storage'));
        }

        processQueue(refreshError, null);

        if (hasWindow && window.location?.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient; 